import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs';
import { STAFF_MODULE, StaffModuleSlug, StaffPermissionSlug } from './permission.constants';
import { PermissionService } from './permission.service';

const STAFF_MODULE_PATH: Record<StaffModuleSlug, string> = {
  [STAFF_MODULE.dashboard]: '/dashboard',
  [STAFF_MODULE.opportunity]: '/opportunities',
  [STAFF_MODULE.loanApplication]: '/loan-application',
  [STAFF_MODULE.feesMaker]: '/fee-maker',
  [STAFF_MODULE.feesChecker]: '/fee-checker',
  [STAFF_MODULE.qualityCheckCpa]: '/quality-check-cpa',
  [STAFF_MODULE.financialAnalysis]: '/financial-analysis',
  [STAFF_MODULE.initiation]: '/initiation',
  [STAFF_MODULE.capturing]: '/capturing',
  [STAFF_MODULE.completion]: '/completion',
  [STAFF_MODULE.underwriter]: '/underwriter',
  [STAFF_MODULE.postSanctionCases]: '/post-sanction-cases',
  [STAFF_MODULE.amendatorySanction]: '/amendatory-sanction-cases',
  [STAFF_MODULE.qualityCheckOperations]: '/quality-check-operations',
  [STAFF_MODULE.disbursedCases]: '/disbursed-cases',
};

const STAFF_MODULE_ORDER: StaffModuleSlug[] = Object.values(STAFF_MODULE);

const firstEnabledModulePath = (
  permissions: PermissionService,
  exclude: StaffModuleSlug,
): string | null => {
  const fallback = STAFF_MODULE_ORDER.find(
    (slug) => slug !== exclude && permissions.hasModule(slug),
  );
  return fallback ? STAFF_MODULE_PATH[fallback] : null;
};

/** Loads action grants for the authenticated shell without gating module access. */
export const loadPermissionsGuard: CanActivateFn = () =>
  inject(PermissionService)
    .load()
    .pipe(map(() => true));

/** Blocks direct URLs for modules hidden by appHasModule. */
export const moduleGuard = (slugCode: StaffModuleSlug): CanActivateFn => {
  return () => {
    const permissions = inject(PermissionService);
    const router = inject(Router);

    return permissions
      .load()
      .pipe(
        map((loaded) => {
          if (loaded && permissions.hasModule(slugCode)) {
            return true;
          }
          // Endpoint replied with an access notice instead of the granted message.
          if (loaded && !permissions.hasAccess()) {
            return router.createUrlTree(['/access-denied']);
          }
          const fallbackPath = firstEnabledModulePath(permissions, slugCode);
          return fallbackPath ? router.createUrlTree([fallbackPath]) : false;
        }),
      );
  };
};

/** Blocks direct URLs for features hidden by appHasPermission. */
export const permissionGuard = (
  slugCode: StaffPermissionSlug,
  fallbackUrl: string,
): CanActivateFn => {
  return () => {
    const permissions = inject(PermissionService);
    const router = inject(Router);

    return permissions
      .load()
      .pipe(
        map((loaded) =>
          loaded && permissions.hasPermission(slugCode)
            ? true
            : router.createUrlTree([fallbackUrl]),
        ),
      );
  };
};
